import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { CopyPitchesDialog } from 'components/common/dialogs/copy-pitches';
import { PitchDataDialog } from 'components/common/dialogs/pitch-data';
import { SharedListDeleteConfirmation } from 'components/common/pitch-lists/shared-list-delete-warning';
import { ChangeVideoDialog } from 'components/sections/pitch-list/dialogs/change-video';
import { DeletePitchesDialog } from 'components/sections/pitch-list/dialogs/delete-pitches';
import { EditBreaksDialog } from 'components/sections/pitch-list/dialogs/edit-breaks';
import { EditPitchDialog } from 'components/sections/pitch-list/dialogs/edit-pitch';
import { EditSpinsDialog } from 'components/sections/pitch-list/dialogs/edit-spins';
import { OptimizePitchDialog } from 'components/sections/pitch-list/dialogs/optimize-pitch';
import { RenameFolderDialog } from 'components/sections/pitch-list/dialogs/rename-folder';
import { ReorderDialog } from 'components/sections/pitch-list/dialogs/reorder-dialog';
import { ResetTrainingDialog } from 'components/sections/pitch-list/dialogs/reset-training';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { AimingContext } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import { ResetPlateMode } from 'enums/machine.enums';
import { PitchListDialog } from 'enums/pitch-lists';
import { PitchDataMode } from 'enums/pitches';
import { t } from 'i18next';
import { TrainPitchesFn } from 'interfaces/function-types';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { first } from 'lodash';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import useListLibraryStore from 'stores/use-list-library-store';

export const PitchListDialogs = (props: { onTrainFn: TrainPitchesFn }) => {
  const navigate = useNavigate();
  const dialog = usePitchListStore((s) => s.dialog);

  const { resetMSHash } = useContext(MachineContext);
  const { pitchBP, setAndSendForBP } = useContext(AimingContext);

  const activeList = useListLibraryStore((s) => s.activeList);
  const activeReadOnly = useListLibraryStore((s) => s.isActiveListReadOnly());

  // close any open dialogs if we change lists
  useEffect(() => {
    usePitchListStore.getState().closeDialog();
  }, [activeList?._id]);

  if (!dialog) {
    return null;
  }

  const { key, mode } = dialog;

  const firstPitch = first(dialog.pitches);

  switch (mode) {
    case PitchListDialog.DeleteList: {
      if (!activeList) {
        return null;
      }

      return (
        <CommonConfirmationDialog
          key={key}
          identifier="DeleteListDialog"
          title={t('common.delete-x', {
            x: t('common.pitch-list'),
          }).toString()}
          content={<SharedListDeleteConfirmation lists={[activeList]} />}
          action={{
            label: 'common.delete',
            color: RADIX.COLOR.DANGER,
            onClick: () => {
              if (!activeList) {
                return;
              }

              useListLibraryStore
                .getState()
                .deleteLists([activeList._id])
                .then((success) => {
                  if (success) {
                    usePitchListStore.getState().closeDialog();
                    navigate(paths.home);
                  }
                });
            },
          }}
          onClose={() => usePitchListStore.getState().closeDialog()}
        />
      );
    }

    case PitchListDialog.DeletePitches: {
      if (!dialog.pitches) {
        return null;
      }

      return (
        <DeletePitchesDialog
          key={key}
          pitches={dialog.pitches}
          onDelete={() => {
            if (!dialog.pitches) {
              return;
            }

            // selected pitch was deleted, the sidebar should be cleared
            if (
              pitchBP &&
              dialog.pitches.map((p) => p._id).includes(pitchBP._id)
            ) {
              setAndSendForBP({
                pitch: undefined,
                resetPlate: ResetPlateMode.Skip,
                sendConfig: undefined,
              });
            }

            resetMSHash();

            // todo: ensure deleted pitch is not in queue
          }}
        />
      );
    }

    case PitchListDialog.RenameFolder: {
      if (!activeList) {
        return null;
      }

      return (
        <RenameFolderDialog
          key={key}
          list={activeList}
          onClose={() => usePitchListStore.getState().closeDialog()}
        />
      );
    }

    case PitchListDialog.ChangeVideo: {
      if (!dialog.pitches) {
        return null;
      }

      return (
        <ChangeVideoDialog
          key={key}
          pitches={dialog.pitches}
          onClose={async (result) => {
            if (!result) {
              usePitchListStore.getState().closeDialog();
              return;
            }

            /** if selected pitch also had its video updated, update video details so the video preview updates */
            const changedPitch = result.find((p) => p._id === pitchBP?._id);
            if (!changedPitch) {
              usePitchListStore.getState().closeDialog();
              return;
            }

            /** force resend => new video needs to be rendered */
            await setAndSendForBP({
              pitch: changedPitch,
              resetPlate: ResetPlateMode.Skip,
              sendConfig: undefined,
            });

            usePitchListStore.getState().closeDialog();
          }}
        />
      );
    }

    case PitchListDialog.Copy: {
      if (!dialog.pitches) {
        return null;
      }

      return (
        <CopyPitchesDialog
          key={key}
          identifier="PitchListCopyPitchesDialog"
          reloadPitches={usePitchListStore.getState().reloadPitches}
          title={t('common.duplicate-x', {
            x: t(
              dialog.pitches.length === 1 ? 'common.pitch' : 'common.pitches'
            ),
          })}
          description={t('pl.select-pitch-list-to-copy-into').toString()}
          pitches={dialog.pitches}
          onCreated={() => usePitchListStore.getState().closeDialog()}
          onClose={() => usePitchListStore.getState().closeDialog()}
          duplicating
        />
      );
    }

    case PitchListDialog.Edit: {
      if (!firstPitch) {
        return null;
      }

      return (
        <EditPitchDialog
          key={key}
          pitch={firstPitch}
          onClose={async () => {
            resetMSHash();
            usePitchListStore.getState().closeDialog();
          }}
        />
      );
    }

    case PitchListDialog.Data: {
      if (!firstPitch) {
        return null;
      }

      return (
        <PitchDataDialog
          key={key}
          identifier="PitchListViewDataDialog"
          mode={PitchDataMode.PitchList}
          pitch={firstPitch}
          onTrainFn={props.onTrainFn}
          onClose={usePitchListStore.getState().closeDialog}
          onPitchUpdated={(updatedPitch) => {
            usePitchListStore.getState().setDialogPitches([updatedPitch]);
          }}
        />
      );
    }

    case PitchListDialog.EditBreaks: {
      if (!firstPitch) {
        return null;
      }

      return (
        <EditBreaksDialog
          key={key}
          pitch={firstPitch}
          readonly={activeReadOnly}
          onClose={async () => {
            resetMSHash();
            usePitchListStore.getState().closeDialog();

            // todo: ensure queue does not contain the now-untrained pitch
          }}
        />
      );
    }

    case PitchListDialog.Optimize: {
      if (!firstPitch) {
        return null;
      }

      return (
        <OptimizePitchDialog
          key={key}
          pitch={firstPitch}
          readonly={activeReadOnly}
          onClose={() => {
            usePitchListStore.getState().closeDialog();

            // todo: ensure queue does not contain the now-untrained pitch
          }}
        />
      );
    }

    case PitchListDialog.EditSpins: {
      if (!firstPitch) {
        return null;
      }

      return (
        <EditSpinsDialog
          key={key}
          pitch={firstPitch}
          readonly={activeReadOnly}
          onClose={async () => {
            resetMSHash();
            usePitchListStore.getState().closeDialog();

            // todo: ensure queue does not contain the now-untrained pitch
          }}
        />
      );
    }

    case PitchListDialog.Reset: {
      if (!dialog.pitches) {
        return null;
      }

      return (
        <ResetTrainingDialog
          key={key}
          pitches={dialog.pitches}
          onClose={() => {
            usePitchListStore.getState().closeDialog();

            // todo: ensure queue does not contain the now-untrained pitch
          }}
        />
      );
    }

    case PitchListDialog.ReorderList: {
      if (!dialog.pitches) {
        return null;
      }

      return (
        <ReorderDialog
          key={key}
          pitches={dialog.pitches}
          onClose={() => {
            usePitchListStore.getState().closeDialog();
          }}
        />
      );
    }

    default: {
      return null;
    }
  }
};
